import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getArticleStats } from '../services/articles';
import { setTitle } from '../utils/environment';
import { useLocation } from 'react-router-dom';

const Stats = () => {
  const { user, logout } = useAuth();
  const { showError } = useToast();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const isInitialMount = useRef(true);

  /**
   * Load article statistics from API
   */
  const loadStats = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const statsData = await getArticleStats(true); // Force refresh
      setStats(statsData);
    } catch (error) {
      const errorMessage = error.message || 'Failed to load statistics';
      console.error('Failed to load article statistics:', error);
      showError(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load stats on mount
  useEffect(() => {
    setTitle('Statistics');
    loadStats();
  }, []);

  // Refresh stats when navigating back to this page
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Force refresh stats when user navigates back to stats page
    loadStats(true);
  }, [location.pathname]);

  // Handle refresh
  const handleRefresh = () => {
    loadStats(true);
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-fun-lg mx-auto mb-4"></div>
          <p className="text-gray-600">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <p className="text-gray-600">No statistics available</p>
      </div>
    );
  }

  // Calculate reading progress percentage
  const readingPercentage = stats.total_articles > 0
    ? Math.round((stats.read_articles / stats.total_articles) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <header className="bg-white shadow-clay border-b-3 border-clay-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => window.history.back()}
                className="mr-4 p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-clay transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="h-10 w-10 mr-3 transition-transform duration-300 hover:animate-bounce-gentle">
                <img src={new URL('../assets/logo.png', import.meta.url).href} alt="Read It Never" className="h-10 w-10 rounded-clay shadow-clay-sm" />
              </div>
              <div>
                <h1 className="font-display text-xl font-bold text-gray-900">Reading Statistics</h1>
                <p className="text-sm text-gray-600">Track your reading habits and progress</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="btn-clay-secondary btn-clay-sm flex items-center disabled:opacity-50"
              >
                <svg className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
              <span className="badge-fun-primary text-xs">
                {user?.email?.split('@')[0]}
              </span>
              <button
                onClick={logout}
                className="btn-clay-ghost text-sm"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Stats */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-fun-sky text-center">
              <p className="font-display text-4xl font-bold text-fun-sky mb-2">{stats.total_articles}</p>
              <p className="text-sm font-medium text-gray-700">Total Articles</p>
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-accent-500 text-center">
              <p className="font-display text-4xl font-bold text-accent-500 mb-2">{stats.unread_articles}</p>
              <p className="text-sm font-medium text-gray-700">Unread</p>
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-fun-mint text-center">
              <p className="font-display text-4xl font-bold text-fun-mint mb-2">{stats.read_articles}</p>
              <p className="text-sm font-medium text-gray-700">Read</p>
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-secondary-500 text-center">
              <p className="font-display text-4xl font-bold text-secondary-500 mb-2">{stats.weekly_read_articles}</p>
              <p className="text-sm font-medium text-gray-700">This Week</p>
              <p className="text-xs text-gray-500 mt-1">articles read</p>
            </div>
          </div>
        </section>

        {/* Reading Progress */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Progress</h2>
          <div className="card-clay p-6">
            <div className="flex justify-between items-center mb-3">
              <p className="text-lg font-semibold text-gray-900">Reading Progress</p>
              <p className="font-display text-xl font-bold text-primary-600">{readingPercentage}%</p>
            </div>
            <div className="w-full bg-clay-bg rounded-clay h-4 mb-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-primary-500 to-secondary-500 h-4 rounded-clay transition-all duration-500"
                style={{ width: `${readingPercentage}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 text-center">
              {stats.read_articles} of {stats.total_articles} articles read
            </p>
          </div>
        </section>

        {/* Activity & Streaks */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Activity</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-primary-500 text-center">
              <p className="font-display text-4xl font-bold text-primary-600 mb-2">{stats.articles_this_month}</p>
              <p className="text-sm font-semibold text-gray-700">This Month</p>
              <p className="text-xs text-gray-500 mt-1">articles saved</p>
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-fun-coral text-center">
              <p className="font-display text-4xl font-bold text-fun-coral mb-2">{stats.reading_streak}</p>
              <p className="text-sm font-semibold text-gray-700">Reading Streak</p>
              <p className="text-xs text-gray-500 mt-1">days in a row 🔥</p>
            </div>
          </div>
        </section>

        {/* Daily Activity Chart */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Daily Activity</h2>
          <div className="card-clay p-6">
            {stats.daily_activity && stats.daily_activity.length > 0 ? (
              <>
                <div className="flex justify-between items-center mb-6">
                  <p className="text-lg font-semibold text-gray-900">Last 30 Days Activity</p>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-primary-500 rounded-clay mr-2"></div>
                      <span className="text-sm text-gray-600">Saved</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-fun-mint rounded-clay mr-2"></div>
                      <span className="text-sm text-gray-600">Read</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-end justify-between h-40 overflow-x-auto">
                  {stats.daily_activity.map((day) => {
                    const maxValue = Math.max(
                      ...stats.daily_activity.map(d => Math.max(d.articles_saved, d.articles_read)),
                      1
                    );
                    const savedHeight = (day.articles_saved / maxValue) * 100;
                    const readHeight = (day.articles_read / maxValue) * 100;
                    // Parse date as local date (YYYY-MM-DD) to avoid timezone conversion
                    const [, month, dayNum] = day.date.split('-').map(Number);
                    const dayLabel = `${month}/${dayNum}`; // Show as "M/D" format (e.g., "10/1")

                    return (
                      <div key={day.date} className="flex flex-col items-center">
                        <div className="h-32 flex items-end justify-center gap-0.5">
                          {/* Saved articles bar (primary) */}
                          <div
                            className="w-1.5 bg-primary-500 rounded-t-clay transition-all duration-300"
                            style={{ height: `${savedHeight}%`, minHeight: day.articles_saved > 0 ? '2px' : '0' }}
                            title={`Saved: ${day.articles_saved}`}
                          ></div>
                          {/* Read articles bar (mint) */}
                          <div
                            className="w-1.5 bg-fun-mint rounded-t-clay transition-all duration-300"
                            style={{ height: `${readHeight}%`, minHeight: day.articles_read > 0 ? '2px' : '0' }}
                            title={`Read: ${day.articles_read}`}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">{dayLabel}</p>
                        <p className="text-xs font-medium text-gray-900">{day.articles_saved}/{day.articles_read}</p>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-gray-600 text-center italic py-8">No daily activity data available</p>
            )}
          </div>
        </section>

        {/* Processing Status */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Processing Status</h2>
          <div className="card-clay p-6">
            {stats.processing_stats && Object.values(stats.processing_stats).some(v => v > 0) ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.processing_stats.completed > 0 && (
                  <div className="text-center p-4 bg-emerald-50 border-2 border-fun-mint rounded-clay">
                    <p className="text-2xl mb-1">✅</p>
                    <p className="font-display text-2xl font-bold text-fun-mint">{stats.processing_stats.completed}</p>
                    <p className="text-sm text-gray-700">Ready to Read</p>
                  </div>
                )}
                {stats.processing_stats.processing > 0 && (
                  <div className="text-center p-4 bg-primary-50 border-2 border-primary-300 rounded-clay">
                    <p className="text-2xl mb-1">⚡</p>
                    <p className="font-display text-2xl font-bold text-primary-600">{stats.processing_stats.processing}</p>
                    <p className="text-sm text-gray-700">Processing</p>
                  </div>
                )}
                {stats.processing_stats.pending > 0 && (
                  <div className="text-center p-4 bg-accent-50 border-2 border-accent-300 rounded-clay">
                    <p className="text-2xl mb-1">⏳</p>
                    <p className="font-display text-2xl font-bold text-accent-600">{stats.processing_stats.pending}</p>
                    <p className="text-sm text-gray-700">Pending</p>
                  </div>
                )}
                {stats.processing_stats.failed > 0 && (
                  <div className="text-center p-4 bg-rose-50 border-2 border-fun-coral rounded-clay">
                    <p className="text-2xl mb-1">❌</p>
                    <p className="font-display text-2xl font-bold text-fun-coral">{stats.processing_stats.failed}</p>
                    <p className="text-sm text-gray-700">Failed</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-600 text-center italic py-4">No processing data available</p>
            )}
          </div>
        </section>

        {/* Top Sources */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Top Sources</h2>
          <div className="card-clay p-6">
            {stats.top_domains && stats.top_domains.length > 0 ? (
              <div className="space-y-3">
                {stats.top_domains.map((domain, index) => (
                  <div key={domain.domain} className="flex justify-between items-center py-3 border-b border-clay-border last:border-b-0 hover:bg-clay-bg rounded-clay px-2 transition-colors">
                    <div className="flex-1">
                      <p className="text-base font-semibold text-gray-900 truncate">{domain.domain}</p>
                      <p className="text-sm text-gray-600">{domain.count} articles</p>
                    </div>
                    <div className="ml-4">
                      <span className={`badge-fun${index === 0 ? '-accent' : index === 1 ? '-secondary' : '-primary'} font-bold`}>#{index + 1}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-center italic py-4">No domains to show yet</p>
            )}
          </div>
        </section>

        {/* Recent Activity */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Recent Activity</h2>
          <div className="card-clay p-6">
            {stats.recent_activity && stats.recent_activity.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_activity.map((activity, index) => {
                  const activityDate = new Date(activity.date);
                  return (
                    <div key={`${activity.date}-${index}`} className="py-3 border-b border-clay-border last:border-b-0 hover:bg-clay-bg rounded-clay px-2 transition-colors">
                      <p className="text-base font-semibold text-gray-900 mb-1 line-clamp-2">
                        {activity.title}
                      </p>
                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <span className="truncate mr-2">{activity.domain}</span>
                        <span>{activityDate.toLocaleDateString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-600 text-center italic py-4">No recent activity</p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Stats;
